/**
 * Progressive polling hook for token balance updates
 *
 * Polls for token balance updates with adaptive intervals:
 * - Active user: every 30 seconds
 * - Inactive user: interval grows up to 5 minutes
 *
 * This ensures real-time balance updates when user is active,
 * while reducing server load when user is idle.
 */

import { useEffect, useRef, useCallback, useState } from 'react';
import { useOrganization } from '../contexts/OrganizationContext';
import { useOrganizationUsage } from '../contexts/OrganizationUsageContext';

const MIN_INTERVAL = 30_000;  // 30 seconds - active user
const MAX_INTERVAL = 300_000; // 5 minutes - inactive user
const INACTIVITY_THRESHOLD = 60_000; // 1 minute without activity → start increasing interval

export function useTokenBalancePolling(enabled: boolean = true) {
  const { currentOrganization } = useOrganization();
  const { refreshUsage } = useOrganizationUsage();
  const [isRefreshing, setIsRefreshing] = useState(false);
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Update last activity timestamp
  const updateActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
  }, []);

  // Calculate next polling interval based on user activity
  const getNextInterval = useCallback(() => {
    const inactiveTime = Date.now() - lastActivityRef.current;

    if (inactiveTime < INACTIVITY_THRESHOLD) {
      return MIN_INTERVAL;
    }

    // Linear increase from MIN to MAX over 5 minutes of inactivity
    const progress = Math.min(inactiveTime / (5 * 60_000), 1);
    return Math.round(MIN_INTERVAL + (MAX_INTERVAL - MIN_INTERVAL) * progress);
  }, []);

  useEffect(() => {
    if (!enabled || !currentOrganization?.id) {
      setIsRefreshing(false);
      return;
    }

    // Event listeners for tracking user activity
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];

    // Throttled activity update to avoid excessive calls
    let activityTimeout: ReturnType<typeof setTimeout> | null = null;
    const throttledUpdateActivity = () => {
      if (activityTimeout) return;
      updateActivity();
      activityTimeout = setTimeout(() => {
        activityTimeout = null;
      }, 1000); // Throttle to max 1 update per second
    };

    events.forEach(event => window.addEventListener(event, throttledUpdateActivity, { passive: true }));

    // Each effect run owns its own polling chain. Without this flag an
    // in-flight poll() re-arms its timer after cleanup has already run,
    // orphaning a chain that no later cleanup can clear.
    let cancelled = false;

    // Polling function
    const poll = async () => {
      if (cancelled) return;

      setIsRefreshing(true);
      try {
        await refreshUsage();
      } catch (error) {
        // Silently handle errors - will retry on next poll
        console.debug('Token balance poll failed:', error);
      } finally {
        if (!cancelled) setIsRefreshing(false);
      }

      if (cancelled) return;

      const nextInterval = getNextInterval();
      timerRef.current = setTimeout(poll, nextInterval);
    };

    // Fetch immediately on mount, then start polling
    poll();

    return () => {
      cancelled = true;
      events.forEach(event => window.removeEventListener(event, throttledUpdateActivity));
      if (timerRef.current) clearTimeout(timerRef.current);
      if (activityTimeout) clearTimeout(activityTimeout);
    };
  }, [enabled, currentOrganization?.id, refreshUsage, updateActivity, getNextInterval]);

  return { isRefreshing };
}
